import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import api from "../../utils/api";
import { logger } from '../../utils/logger';

// Intent labels for display
const INTENT_LABELS = {
  sales: "Sales",
  support: "Support",
  hr: "HR & Careers",
  general: "General",
  partnership: "Partnership",
  billing: "Billing"
};

// Domain labels for display
const DOMAIN_LABELS = {
  it_software: "IT & Software",
  ecommerce: "E-commerce",
  healthcare: "Healthcare",
  finance: "Finance",
  education: "Education",
  manufacturing: "Manufacturing",
  consulting: "Consulting",
  hospitality: "Hospitality",
  real_estate: "Real Estate",
  general: "General"
};

const PersonasPage = () => {
  const { agent } = useOutletContext();
  const [personas, setPersonas] = useState({});
  const [detectedDomain, setDetectedDomain] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [intents, setIntents] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testMessage, setTestMessage] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [editingPersona, setEditingPersona] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPersona, setNewPersona] = useState({
    intent: "sales",
    domain: "",
    name: "",
    tone: "",
    traits: "",
    opening_style: "",
    cta_style: ""
  });

  useEffect(() => {
    fetchPersonas();
    fetchDomain();
  }, []);

  const fetchPersonas = async () => {
    try {
      const response = await api.get("/api/personas");
      if (response.data.success) {
        setPersonas(response.data.personas);
        setIntents(response.data.intents);
        setDomains(response.data.domains);
      }
    } catch (error) {
      logger.error("Error fetching personas:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDomain = async () => {
    try {
      const response = await api.get("/api/personas/domain");
      if (response.data.success) {
        setDetectedDomain(response.data.detected_domain);
        setCompanyName(response.data.company_name || "Your Company");
      }
    } catch (error) {
      logger.error("Error fetching domain:", error);
    }
  };

  const handleTestIntent = async () => {
    if (!testMessage.trim()) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const response = await api.post("/api/personas/test-intent", {
        message: testMessage
      });
      if (response.data.success) {
        setTestResult(response.data);
      }
    } catch (error) {
      logger.error("Error testing intent:", error);
    } finally {
      setIsTesting(false);
    }
  };

  const handleRefreshCache = async () => {
    try {
      const response = await api.post("/api/personas/refresh-cache");
      if (response.data.success) {
        setDetectedDomain(response.data.detected_domain);
        fetchPersonas();
        alert("Cache refreshed successfully!");
      }
    } catch (error) {
      logger.error("Error refreshing cache:", error);
      alert("Failed to refresh cache");
    }
  };

  const handleCreatePersona = async () => {
    try {
      const traitsArray = newPersona.traits.split("\n").filter(t => t.trim());
      const response = await api.post("/api/personas/create", {
        intent: newPersona.intent,
        domain: newPersona.domain || null,
        name: newPersona.name,
        tone: newPersona.tone,
        traits: traitsArray,
        opening_style: newPersona.opening_style,
        cta_style: newPersona.cta_style
      });
      if (response.data.success) {
        setShowCreateModal(false);
        setNewPersona({
          intent: "sales",
          domain: "",
          name: "",
          tone: "",
          traits: "",
          opening_style: "",
          cta_style: ""
        });
        fetchPersonas();
        alert("Persona created successfully!");
      }
    } catch (error) {
      logger.error("Error creating persona:", error);
      alert(error.response?.data?.detail || "Failed to create persona");
    }
  };

  const handleUpdatePersona = async (personaKey, updates) => {
    try {
      const response = await api.put("/api/personas/update", {
        persona_key: personaKey,
        ...updates
      });
      if (response.data.success) {
        fetchPersonas();
        setEditingPersona(null);
        alert("Persona updated successfully!");
      }
    } catch (error) {
      logger.error("Error updating persona:", error);
      alert("Failed to update persona");
    }
  };

  const handleDeletePersona = async (personaKey) => {
    if (!window.confirm(`Delete custom persona "${personaKey}"? This will revert to the default.`)) {
      return;
    }
    try {
      const response = await api.delete(`/api/personas/${personaKey}`);
      if (response.data.success) {
        fetchPersonas();
        alert("Custom persona deleted. Default will be used.");
      }
    } catch (error) {
      logger.error("Error deleting persona:", error);
      alert(error.response?.data?.detail || "Failed to delete persona");
    }
  };

  // Group personas by intent
  const groupedPersonas = Object.entries(personas).reduce((acc, [key, persona]) => {
    const intent = key.split("_")[0];
    if (!acc[intent]) acc[intent] = [];
    acc[intent].push({ key, ...persona });
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bot Personas</h1>
            <p className="text-gray-600 mt-1">
              Configure how the bot adapts its personality based on user intent and your industry
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Custom Persona
          </button>
        </div>

        {/* Domain Detection Card */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Detected Company Domain</h3>
                <p className="text-gray-600 text-sm">Based on your scraped website content</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {DOMAIN_LABELS[detectedDomain] || detectedDomain}
                </span>
                <p className="text-sm text-gray-500 mt-1">{companyName}</p>
              </div>
              <button
                onClick={handleRefreshCache}
                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                title="Refresh detection"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Intent Tester */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Test Intent Detection</h3>
          <p className="text-gray-600 text-sm mb-4">
            Enter a sample message to see which persona would be selected
          </p>
          <div className="flex gap-3">
            <input
              type="text"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="e.g., 'How much does your product cost?' or 'Are you hiring?'"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              onKeyDown={(e) => e.key === 'Enter' && handleTestIntent()}
            />
            <button
              onClick={handleTestIntent}
              disabled={isTesting || !testMessage.trim()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTesting ? "Testing..." : "Test"}
            </button>
          </div>

          {testResult && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-500">Detected Intent</span>
                  <p className="font-medium text-gray-900">{INTENT_LABELS[testResult.detected_intent]}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Company Domain</span>
                  <p className="font-medium text-gray-900">{DOMAIN_LABELS[testResult.detected_domain]}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-sm text-gray-500">Selected Persona</span>
                  <p className="font-medium text-gray-900">{testResult.selected_persona.name}</p>
                  <p className="text-sm text-gray-600 mt-1">Tone: {testResult.selected_persona.tone}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Personas by Intent */}
        {Object.entries(groupedPersonas).map(([intent, intentPersonas]) => (
          <div key={intent} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">
                {INTENT_LABELS[intent] || intent.charAt(0).toUpperCase() + intent.slice(1)} Personas
              </h3>
              <p className="text-sm text-gray-600">
                {intent === 'sales' && "Used when users ask about pricing, demos, or products"}
                {intent === 'support' && "Used when users need help or have technical issues"}
                {intent === 'hr' && "Used when users ask about jobs, careers, or hiring"}
                {intent === 'general' && "Default persona for general inquiries"}
                {intent === 'partnership' && "Used for B2B and partnership inquiries"}
                {intent === 'billing' && "Used for billing and payment questions"}
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {intentPersonas.map((persona) => (
                <div key={persona.key} className="p-6">
                  {editingPersona === persona.key ? (
                    <PersonaEditor
                      persona={persona}
                      onSave={(updates) => handleUpdatePersona(persona.key, updates)}
                      onCancel={() => setEditingPersona(null)}
                    />
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium text-gray-900">{persona.name}</h4>
                          {persona.is_custom && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                              Custom
                            </span>
                          )}
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            {persona.key}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Tone:</span> {persona.tone}
                        </p>
                        {persona.traits && persona.traits.length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm font-medium text-gray-700">Traits:</span>
                            <ul className="mt-1 space-y-1">
                              {persona.traits.slice(0, 3).map((trait, i) => (
                                <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                                  <span className="text-green-500">•</span>
                                  {trait}
                                </li>
                              ))}
                              {persona.traits.length > 3 && (
                                <li className="text-sm text-gray-400">+{persona.traits.length - 3} more...</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingPersona(persona.key)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Edit persona"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {persona.is_custom && (
                          <button
                            onClick={() => handleDeletePersona(persona.key)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete custom persona"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Create Persona Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Create Custom Persona</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Create a persona tailored to your specific needs
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Intent *</label>
                    <select
                      value={newPersona.intent}
                      onChange={(e) => setNewPersona({ ...newPersona, intent: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      {intents.map((intent) => (
                        <option key={intent} value={intent}>{INTENT_LABELS[intent] || intent}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Domain (optional)</label>
                    <select
                      value={newPersona.domain}
                      onChange={(e) => setNewPersona({ ...newPersona, domain: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">General (all domains)</option>
                      {domains.map((domain) => (
                        <option key={domain} value={domain}>{DOMAIN_LABELS[domain] || domain}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Persona Name *</label>
                  <input
                    type="text"
                    value={newPersona.name}
                    onChange={(e) => setNewPersona({ ...newPersona, name: e.target.value })}
                    placeholder="e.g., Enterprise Sales Specialist"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tone *</label>
                  <input
                    type="text"
                    value={newPersona.tone}
                    onChange={(e) => setNewPersona({ ...newPersona, tone: e.target.value })}
                    placeholder="e.g., professional, consultative, solution-focused"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Traits (one per line) *</label>
                  <textarea
                    value={newPersona.traits}
                    onChange={(e) => setNewPersona({ ...newPersona, traits: e.target.value })}
                    placeholder="Emphasizes ROI and business value&#10;Asks discovery questions&#10;Offers custom solutions"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opening Style</label>
                  <input
                    type="text"
                    value={newPersona.opening_style}
                    onChange={(e) => setNewPersona({ ...newPersona, opening_style: e.target.value })}
                    placeholder="e.g., I'd be happy to help you explore enterprise solutions."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Call-to-Action Style</label>
                  <input
                    type="text"
                    value={newPersona.cta_style}
                    onChange={(e) => setNewPersona({ ...newPersona, cta_style: e.target.value })}
                    placeholder="e.g., Would you like to schedule a consultation with our team?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePersona}
                  disabled={!newPersona.name || !newPersona.tone || !newPersona.traits}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Persona
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Persona Editor Component
const PersonaEditor = ({ persona, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: persona.name || "",
    tone: persona.tone || "",
    traits: (persona.traits || []).join("\n"),
    opening_style: persona.opening_style || "",
    cta_style: persona.cta_style || ""
  });

  const handleSave = () => {
    onSave({
      name: form.name,
      tone: form.tone,
      traits: form.traits.split("\n").filter(t => t.trim()),
      opening_style: form.opening_style,
      cta_style: form.cta_style
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
          <input
            type="text"
            value={form.tone}
            onChange={(e) => setForm({ ...form, tone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Traits (one per line)</label>
        <textarea
          value={form.traits}
          onChange={(e) => setForm({ ...form, traits: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Opening Style</label>
          <input
            type="text"
            value={form.opening_style}
            onChange={(e) => setForm({ ...form, opening_style: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">CTA Style</label>
          <input
            type="text"
            value={form.cta_style}
            onChange={(e) => setForm({ ...form, cta_style: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default PersonasPage;
